import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserService } from 'libs/core/src/lib/services/user.service';
import { SkeletonTemplateService } from '@skeleton-loader';
import { CapitalFirstLetterPipe, PostalCodePipe } from '../pipes';
import { CheckoutAddress, PlayerAddress, ShippingDetails } from '@models';

@Component({
  selector: 'lib-shipping-information',
  templateUrl: './shipping-information.component.html',
  styleUrls: ['./shipping-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatTooltipModule, CapitalFirstLetterPipe, PostalCodePipe],
})
export class ShippingInformationComponent {
  readonly shippingAddress = input.required<CheckoutAddress | PlayerAddress | ShippingDetails | null>();
  readonly email = input<string | null>();

  addressTooltip =
    'Shipments cannot be delivered to PO Box addresses, United States territories, or addresses outside of the United States.';

  readonly skeletonService = inject(SkeletonTemplateService);
  userService = inject(UserService);

  // Stored order details carry their own name and phone; the casino profile has a name only.
  name = computed(() => {
    const addr = this.shippingAddress();
    return addr && 'firstName' in addr
      ? { firstName: addr.firstName, lastName: addr.lastName }
      : { firstName: this.userService.firstName(), lastName: this.userService.lastName() };
  });
  phone = computed(() => {
    const addr = this.shippingAddress();
    return addr && 'telephoneNumber' in addr ? addr.telephoneNumber : '';
  });

  address = computed(() => {
    const addr = this.shippingAddress();
    if (!addr) return null;
    return {
      addressLine1: addr.addressLine1,
      addressLine2: 'addressLine2' in addr ? addr.addressLine2 : '',
      addressLine3: 'addressLine3' in addr ? addr.addressLine3 : '',
      city: addr.city,
      state: 'stateCode' in addr ? addr.stateCode : addr.state,
      postalCode: addr.postalCode,
      countryCode: 'country' in addr ? addr.country : addr.countryCode,
    };
  });
}
